import { useRouter } from 'expo-router';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Radii, Spacing } from '@/constants/theme';

export interface LegalSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

interface LegalDocumentScreenProps {
  title: string;
  intro: string;
  sections: LegalSection[];
}

export function LegalDocumentScreen({ title, intro, sections }: LegalDocumentScreenProps) {
  const router = useRouter();

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.back()}
            style={({ pressed }) => [styles.backBtn, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Kembali"
          >
            <Text style={styles.backArrow}>←</Text>
          </Pressable>
          <ThemedText type="subtitle" style={styles.headerTitle}>{title}</ThemedText>
          <View style={styles.headerSpacer} />
        </View>

        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.introCard}>
            <Text style={styles.intro}>{intro}</Text>
            <Text style={styles.updated}>Diperbarui 21 Agustus 2026</Text>
          </View>

          {sections.map((section) => (
            <View key={section.heading} style={styles.section}>
              <Text style={styles.sectionHeading}>{section.heading}</Text>
              {section.paragraphs.map((paragraph, index) => (
                <Text key={`${section.heading}-paragraph-${index}`} style={styles.paragraph}>
                  {paragraph}
                </Text>
              ))}
              {section.bullets?.map((bullet) => (
                <View key={bullet} style={styles.bulletRow}>
                  <Text style={styles.bulletMark}>•</Text>
                  <Text style={styles.bulletText}>{bullet}</Text>
                </View>
              ))}
            </View>
          ))}
        </ScrollView>
      </SafeAreaView>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e5',
  },
  backBtn: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backArrow: { fontSize: 24, color: Brand.primary, fontWeight: '600' },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: 18,
    fontWeight: '700',
    color: '#1A0B5C',
  },
  headerSpacer: { width: 40 },
  scrollContent: { padding: Spacing.four, paddingBottom: Spacing.six },
  introCard: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    padding: Spacing.four,
    gap: Spacing.two,
  },
  intro: { color: '#201c23', fontSize: 15, lineHeight: 23 },
  updated: { color: '#6b6573', fontSize: 12 },
  section: { marginTop: Spacing.four, gap: Spacing.two },
  sectionHeading: { color: '#1A0B5C', fontSize: 17, fontWeight: '700' },
  paragraph: { color: '#201c23', fontSize: 15, lineHeight: 23 },
  bulletRow: { flexDirection: 'row', alignItems: 'flex-start', gap: Spacing.two },
  bulletMark: { color: Brand.primary, fontSize: 18, lineHeight: 23 },
  bulletText: { flex: 1, color: '#201c23', fontSize: 15, lineHeight: 23 },
  btnPressed: { opacity: 0.7 },
});
